import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import { FiEdit2, FiKey, FiPlus, FiX } from "react-icons/fi";
import AdminLayout from "../layouts/AdminLayout";
import { services } from "../services";

type AdminType = "super" | "admin" | "viewer_admin" | "state" | "pp_admin";

type AdminRecord = {
  id: bigint;
  name: string;
  email: string | null;
  mobileNumber: string | null;
  type: AdminType;
  stateId: bigint | null;
  placementPartnerId: bigint | null;
  stateName: string | null;
  placementPartnerName: string | null;
  createdAt: string;
  updatedAt: string;
};

type StateOption = {
  id: bigint;
  name: string;
};

type PlacementPartnerOption = {
  id: bigint;
  name: string;
};

type AdminForm = {
  name: string;
  email: string;
  mobileNumber: string;
  type: AdminType;
  stateId: string;
  placementPartnerId: string;
  password: string;
};

const initialForm: AdminForm = {
  name: "",
  email: "",
  mobileNumber: "",
  type: "viewer_admin",
  stateId: "",
  placementPartnerId: "",
  password: "",
};

const roleLabels: Record<AdminType, string> = {
  super: "Super Admin",
  admin: "Central Admin",
  viewer_admin: "Admin",
  state: "State Admin",
  pp_admin: "PP Admin",
};

export default function AdminManagementPage() {
  const [admins, setAdmins] = useState<AdminRecord[]>([]);
  const [states, setStates] = useState<StateOption[]>([]);
  const [placementPartners, setPlacementPartners] = useState<
    PlacementPartnerOption[]
  >([]);
  const [form, setForm] = useState<AdminForm>(initialForm);
  const [editingAdmin, setEditingAdmin] = useState<AdminRecord | null>(null);
  const [passwordTarget, setPasswordTarget] = useState<AdminRecord | null>(
    null,
  );
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchAdmins = useCallback(async () => {
    setIsLoading(true);
    try {
      const records = await services.requestSvc.get<AdminRecord[]>("/admins", {
        withCredentials: true,
      });
      setAdmins(records || []);
    } catch (error: any) {
      toast.error(error?.message || "Failed to fetch admins.");
      setAdmins([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchOptions = useCallback(async () => {
    try {
      const [stateRecords, placementPartnerRecords] = await Promise.all([
        services.requestSvc.get<StateOption[]>("/states", {
          withCredentials: true,
        }),
        services.requestSvc.get<PlacementPartnerOption[]>("/placement-partners", {
          withCredentials: true,
        }),
      ]);
      setStates(stateRecords || []);
      setPlacementPartners(placementPartnerRecords || []);
    } catch (error) {
      setStates([]);
      setPlacementPartners([]);
    }
  }, []);

  useEffect(() => {
    void fetchAdmins();
    void fetchOptions();
  }, [fetchAdmins, fetchOptions]);

  const resetForm = useCallback(() => {
    setForm(initialForm);
    setEditingAdmin(null);
    setIsModalOpen(false);
  }, []);

  const openCreateModal = () => {
    setForm(initialForm);
    setEditingAdmin(null);
    setIsModalOpen(true);
  };

  const openEditModal = (admin: AdminRecord) => {
    setEditingAdmin(admin);
    setForm({
      name: admin.name,
      email: admin.email || "",
      mobileNumber: admin.mobileNumber || "",
      type: admin.type,
      stateId: admin.stateId ? String(admin.stateId) : "",
      placementPartnerId: admin.placementPartnerId
        ? String(admin.placementPartnerId)
        : "",
      password: "",
    });
    setIsModalOpen(true);
  };

  const handleFormTypeChange = (type: AdminType) => {
    setForm((current) => ({
      ...current,
      type,
      stateId: type === "state" ? current.stateId : "",
      placementPartnerId: type === "pp_admin" ? current.placementPartnerId : "",
      password: editingAdmin ? current.password : current.password,
    }));
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      toast.error("Name is required.");
      return false;
    }
    if (!form.email.trim()) {
      toast.error("Email is required.");
      return false;
    }
    if (!editingAdmin && form.password.trim().length < 8) {
      toast.error("Password must be at least 8 characters.");
      return false;
    }
    if (form.type === "state" && !form.stateId) {
      toast.error("Please select a state for State Admin.");
      return false;
    }
    if (form.type === "pp_admin" && !form.placementPartnerId) {
      toast.error("Please select a placement partner for PP Admin.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      mobileNumber: form.mobileNumber.trim() || undefined,
      type: form.type,
      stateId: form.type === "state" ? form.stateId : null,
      placementPartnerId:
        form.type === "pp_admin" ? form.placementPartnerId : null,
      ...(editingAdmin ? {} : { password: form.password }),
    };

    setIsSaving(true);
    try {
      if (editingAdmin) {
        await services.requestSvc.put(`/admins/${String(editingAdmin.id)}`, payload, {
          withCredentials: true,
        });
        toast.success("Admin updated successfully.");
      } else {
        await services.requestSvc.post("/admins", payload, {
          withCredentials: true,
        });
        toast.success("Admin created successfully.");
      }

      resetForm();
      await fetchAdmins();
    } catch (error: any) {
      toast.error(
        error?.message ||
          `Failed to ${editingAdmin ? "update" : "create"} admin.`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (!passwordTarget) return;
    if (password.trim().length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }

    setIsUpdatingPassword(true);
    try {
      await services.requestSvc.put(
        `/admins/${String(passwordTarget.id)}/password`,
        { password },
        { withCredentials: true },
      );
      toast.success("Password updated successfully.");
      setPassword("");
      setPasswordTarget(null);
    } catch (error: any) {
      toast.error(error?.message || "Failed to update password.");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        name: "Name",
        selector: (row: AdminRecord) => row.name,
        width: "14rem",
      },
      {
        name: "Email",
        selector: (row: AdminRecord) => row.email || "-",
        width: "18rem",
      },
      {
        name: "Mobile",
        selector: (row: AdminRecord) => row.mobileNumber || "-",
        width: "12rem",
      },
      {
        name: "Role",
        selector: (row: AdminRecord) => roleLabels[row.type],
        width: "12rem",
      },
      {
        name: "State",
        selector: (row: AdminRecord) => row.stateName || "-",
        width: "12rem",
      },
      {
        name: "Placement Partner",
        selector: (row: AdminRecord) => row.placementPartnerName || "-",
        width: "16rem",
      },
      {
        name: "Updated",
        selector: (row: AdminRecord) =>
          row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : "-",
        width: "10rem",
      },
      {
        name: "Actions",
        width: "10rem",
        cell: (row: AdminRecord) => (
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="text-blue-700"
              onClick={() => openEditModal(row)}
              title="Edit admin"
            >
              <FiEdit2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="text-gray-700"
              onClick={() => {
                setPassword("");
                setPasswordTarget(row);
              }}
              title="Update password"
            >
              <FiKey className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <AdminLayout title="Create Admin">
      <div className="max-w-7xl flex flex-col gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                Admin Management
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Create admins, update their role assignments, and reset
                passwords.
              </p>
            </div>
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-2"
              onClick={openCreateModal}
            >
              <FiPlus className="h-4 w-4" />
              Add Admin
            </button>
          </div>

          <DataTable<AdminRecord>
            columns={columns}
            data={admins}
            progressPending={isLoading}
            pagination
            className="w-full mt-6"
            noDataComponent="No admins found."
          />
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  {editingAdmin ? "Edit Admin" : "Create Admin"}
                </h2>
              </div>
              <button
                type="button"
                className="text-gray-500"
                onClick={resetForm}
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                className="input-primary"
                placeholder="Name"
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, name: event.target.value }))
                }
              />
              <input
                className="input-primary"
                placeholder="Email"
                type="email"
                value={form.email}
                onChange={(event) =>
                  setForm((current) => ({ ...current, email: event.target.value }))
                }
              />
              <input
                className="input-primary"
                placeholder="Mobile Number"
                value={form.mobileNumber}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    mobileNumber: event.target.value.replace(/\D/g, "").slice(0, 10),
                  }))
                }
              />
              <select
                className="input-primary"
                value={form.type}
                onChange={(event) => handleFormTypeChange(event.target.value as AdminType)}
              >
                {Object.entries(roleLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <select
                className="input-primary"
                value={form.stateId}
                onChange={(event) =>
                  setForm((current) => ({ ...current, stateId: event.target.value }))
                }
                disabled={form.type !== "state"}
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={String(state.id)} value={String(state.id)}>
                    {state.name}
                  </option>
                ))}
              </select>
              <select
                className="input-primary"
                value={form.placementPartnerId}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    placementPartnerId: event.target.value,
                  }))
                }
                disabled={form.type !== "pp_admin"}
              >
                <option value="">Select Placement Partner</option>
                {placementPartners.map((partner) => (
                  <option key={String(partner.id)} value={String(partner.id)}>
                    {partner.name}
                  </option>
                ))}
              </select>
              {!editingAdmin && (
                <input
                  className="input-primary md:col-span-2"
                  placeholder="Password"
                  type="password"
                  value={form.password}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      password: event.target.value,
                    }))
                  }
                />
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                className="btn-secondary"
                onClick={resetForm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void handleSubmit()}
                disabled={isSaving}
              >
                {isSaving
                  ? "Saving..."
                  : editingAdmin
                  ? "Save Changes"
                  : "Create Admin"}
              </button>
            </div>
          </div>
        </div>
      )}

      {passwordTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  Update Password
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Set a new password for {passwordTarget.name}.
                </p>
              </div>
              <button
                type="button"
                className="text-gray-500"
                onClick={() => {
                  setPassword("");
                  setPasswordTarget(null);
                }}
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5">
              <input
                className="input-primary w-full"
                placeholder="New Password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setPassword("");
                  setPasswordTarget(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void handlePasswordUpdate()}
                disabled={isUpdatingPassword}
              >
                {isUpdatingPassword ? "Saving..." : "Update Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
